import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTH_ROUTES } from '../constants';
import { useFlowProtection } from '../../hooks/useFlowProtection';

const FLOW_STORAGE_KEY = 'fromForgotPassword';

const CheckInboxContent: React.FC<{ email: string }> = ({ email }) => {
    const navigate = useNavigate();

    const handleBackToSignIn = () => {
        // Clear flow data when going back
        sessionStorage.removeItem(FLOW_STORAGE_KEY);
        navigate(AUTH_ROUTES.LOGIN);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
             style={{
                 background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
             }}>
            
            {/* Background Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] opacity-90"></div>
            
            {/* Content */}
            <div className="relative z-10 text-center text-white max-w-md mx-auto">
                {/* Logo */}
                <div className="mb-12">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        className="h-12 w-auto mx-auto"
                        style={{
                            filter: 'brightness(0) saturate(100%) invert(100%)'
                        }}
                    />
                </div>

                {/* Title */}
                <h1 
                    style={{
                        fontFamily: 'Manrope',
                        fontWeight: 700,
                        fontSize: 'clamp(28px, 5vw, 36px)',
                        color: '#FFFFFF',
                        marginBottom: '24px',
                        lineHeight: '1.2'
                    }}
                >
                    Check your inbox
                </h1>

                {/* Description */}
                <p 
                    style={{
                        fontFamily: 'Manrope',
                        fontSize: 'clamp(14px, 2vw, 16px)',
                        color: '#BFDBFE',
                        marginBottom: '48px',
                        lineHeight: '1.6',
                        maxWidth: '400px',
                        margin: '0 auto 48px'
                    }}
                >
                    We've sent a password reset link to {email}. It might take a minute or two to arrive.
                    <br />
                    Be sure to check your spam folder too.
                </p>

                {/* Email Icon */}
                <div 
                    style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 48px',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <div
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'rgba(59, 130, 246, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path 
                                d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" 
                                stroke="white" 
                                strokeWidth={1.5} 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </div>

                {/* Back to Sign In Button */}
                <button
                    onClick={handleBackToSignIn}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#BFDBFE',
                        fontSize: '16px',
                        fontFamily: 'Manrope',
                        cursor: 'pointer',
                        padding: '12px 0',
                        margin: '0 auto',
                        transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#BFDBFE'}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" stroke="currentColor"/>
                    </svg>
                    Back to sign in
                </button>
            </div>
        </div>
    );
};

/**
 * CheckInbox Page
 * Protected by useFlowProtection - only accessible after forgot password flow
 * Direct URL access redirects to login page
 * Uses Session Storage to persist on page refresh (user might refresh while waiting for email)
 */
const CheckInbox: React.FC = () => {
    const { isValidAccess, flowData } = useFlowProtection({
        storageKey: FLOW_STORAGE_KEY,
        fallbackPath: AUTH_ROUTES.LOGIN
    });

    // Show nothing while checking or if invalid
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    const email = (flowData?.email as string) || 'your email';

    return <CheckInboxContent email={email} />;
};

export default CheckInbox;
